import { get } from 'svelte/store';
import { WindowsCustomEventTypes } from '../enums/windows-custom-event.enum';
import type {
  IChatKitAction,
  IChatKitWidgetComponentConfig,
  IChatKitWidgetInstance,
  IChatKitWidgetNode,
  IWidgetRuntimeConfig,
} from '../interfaces/chatkit-widget.interface';
import type { IToolCall } from '../interfaces/tool-call.interface';
import { ConfigStore } from '../store/config';
import { defaultLogger as logger } from '../telemetry/logger';
import { compileChatKitJsxTemplate } from './chatkit-jsx-compiler';

const FULL_PLACEHOLDER = /^\s*\{\{\s*([^{}]+?)\s*\}\}\s*$/;
const INLINE_PLACEHOLDER = /\{\{\s*([^{}]+?)\s*\}\}/g;
const ROOT_TYPES = new Set(['Basic', 'Card', 'ListView']);

export interface WidgetRenderResult {
  instance: IChatKitWidgetInstance;
  response: {
    rendered: true;
    widget: string;
    action: 'rendered';
    tool_call_id: string;
    message: string;
  };
}

export const ChatKitWidgetHelper = {
  getWidgetConfig(): IWidgetRuntimeConfig | undefined {
    return get(ConfigStore).widget;
  },

  getComponentConfig(
    functionName: string | null | undefined
  ): IChatKitWidgetComponentConfig | undefined {
    if (!functionName) return undefined;
    const component = this.getWidgetConfig()?.components?.[functionName];
    if (!component || typeof component !== 'object') return undefined;
    if ('template' in component) {
      return component as IChatKitWidgetComponentConfig;
    }
    const node = component as IChatKitWidgetNode;
    return {
      template: node,
      continueAfterRender: node.continueAfterRender === true,
    };
  },

  isWidgetToolCall(toolCall: IToolCall): boolean {
    return !!this.getComponentConfig(toolCall.function?.name);
  },

  shouldContinueAfterRender(toolCall: IToolCall): boolean {
    return this.getComponentConfig(toolCall.function?.name)?.continueAfterRender === true;
  },

  splitToolCalls(toolCalls: IToolCall[]): {
    widgetToolCalls: IToolCall[];
    normalToolCalls: IToolCall[];
  } {
    return toolCalls.reduce(
      (acc, toolCall) => {
        if (this.isWidgetToolCall(toolCall)) {
          acc.widgetToolCalls.push(toolCall);
        } else {
          acc.normalToolCalls.push(toolCall);
        }
        return acc;
      },
      { widgetToolCalls: [], normalToolCalls: [] } as {
        widgetToolCalls: IToolCall[];
        normalToolCalls: IToolCall[];
      }
    );
  },

  getRenderKey(widget: IChatKitWidgetInstance): string {
    return `${widget.functionName}:${stableSerialize(widget.data)}`;
  },

  renderToolCall(toolCall: IToolCall): WidgetRenderResult | null {
    logger.debug('Rendering ChatKit widget tool call', toolCall);
    const functionName = toolCall.function?.name;
    const component = this.getComponentConfig(functionName);
    if (!functionName || !component) return null;

    const args = parseArguments(toolCall);
    const data = {
      ...(component.data ?? {}),
      ...(!isRootWidget(args) ? args : {}),
    };
    const sourceTemplate = isRootWidget(args) ? args : component.template;
    if (!sourceTemplate) {
      logger.warn('ChatKit widget template not found', functionName);
      return null;
    }

    const template = renderTemplate(component, sourceTemplate, data);
    if (!template) return null;

    return {
      instance: {
        id: `${toolCall.id || functionName}-${Date.now()}`,
        toolCallId: toolCall.id,
        functionName,
        template,
        data,
        renderedAt: new Date().toISOString(),
      },
      response: {
        rendered: true,
        widget: functionName,
        action: 'rendered',
        tool_call_id: toolCall.id,
        message:
          'The widget is visible to the user and positioned above any future content. Do not call the same widget again with unchanged data.',
      },
    };
  },

  handleAction(
    action: IChatKitAction,
    item: {
      functionName?: string;
      widgetId?: string;
      questionId?: string;
      data?: any;
    },
    formPayload?: Record<string, any>
  ): void {
    const payload = mergePayload(action.payload ?? {}, formPayload ?? {});
    const actionWithPayload = { ...action, payload };
    window.dispatchEvent(
      new CustomEvent(WindowsCustomEventTypes.chatKitWidgetAction, {
        detail: {
          action: actionWithPayload,
          item,
        },
      })
    );

    void executeAction(actionWithPayload);
  },
};

function stableSerialize(value: any): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

async function executeAction(action: IChatKitAction): Promise<void> {
  const payload = action.payload ?? {};
  const fon = get(ConfigStore).functionObjectName;
  const gqaFunctions = (window as any)[fon];
  const jsfunc = action.type && gqaFunctions && gqaFunctions[action.type];

  if (!jsfunc) {
    logger.warn('ChatKit action function not found', action.type);
    return;
  }

  try {
    await jsfunc(payload);
  } catch (error) {
    logger.error('Error executing ChatKit action function', error);
  }
}

function renderTemplate(
  component: IChatKitWidgetComponentConfig,
  sourceTemplate: IChatKitWidgetNode | string,
  data: Record<string, any>
): IChatKitWidgetNode | null {
  try {
    if (
      typeof sourceTemplate === 'string' &&
      (component.templateFormat === 'chatkit-jsx' ||
        sourceTemplate.trim().startsWith('<'))
    ) {
      return hydrateWidget(
        compileChatKitJsxTemplate(sourceTemplate, data),
        data
      ) as IChatKitWidgetNode;
    }

    if (typeof sourceTemplate === 'string') {
      return hydrateWidget(JSON.parse(sourceTemplate), data) as IChatKitWidgetNode;
    }

    return hydrateWidget(sourceTemplate, data) as IChatKitWidgetNode;
  } catch (error) {
    logger.warn('Unable to render ChatKit widget template', error);
    return null;
  }
}

function parseArguments(toolCall: IToolCall): Record<string, any> {
  const raw = toolCall.function?.arguments;
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    logger.warn('Unable to parse ChatKit widget arguments', error);
    return {};
  }
}

function isRootWidget(value: any): value is IChatKitWidgetNode {
  return !!value && typeof value === 'object' && ROOT_TYPES.has(value.type);
}

function hydrateWidget(value: any, data: Record<string, any>): any {
  if (Array.isArray(value)) return value.map((item) => hydrateWidget(item, data));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key, hydrateWidget(child, data)])
    );
  }
  if (typeof value !== 'string') return value;

  const full = value.match(FULL_PLACEHOLDER);
  if (full) {
    return resolvePath(data, full[1].trim());
  }
  return interpolateString(value, data) ?? '';
}

function interpolateString(
  value: string | undefined,
  data: Record<string, any>
): string | undefined {
  if (!value) return value;
  return value.replace(INLINE_PLACEHOLDER, (_match, path) => {
    const resolved = resolvePath(data, String(path).trim());
    return resolved == null ? '' : String(resolved);
  });
}

function resolvePath(data: Record<string, any>, path: string): any {
  const value = path.split('.').reduce((current: any, segment) => {
    if (current == null) return undefined;
    return current[segment];
  }, data);
  if (typeof value === 'undefined') {
    logger.warn('ChatKit widget placeholder not found', path);
    return '';
  }
  return value;
}

function mergePayload(
  actionPayload: Record<string, any>,
  formPayload: Record<string, any>
): Record<string, any> {
  const payload = structuredCloneSafe(actionPayload);
  Object.entries(formPayload).forEach(([key, value]) => {
    setDottedValue(payload, key, value);
  });
  return payload;
}

function setDottedValue(target: Record<string, any>, path: string, value: any) {
  const parts = path.split('.').filter(Boolean);
  if (!parts.length) return;
  let current = target;
  parts.slice(0, -1).forEach((part) => {
    if (
      !Object.prototype.hasOwnProperty.call(current, part) ||
      current[part] == null ||
      typeof current[part] !== 'object'
    ) {
      current[part] = {};
    }
    current = current[part];
  });
  const finalKey = parts[parts.length - 1];
  current[finalKey] = value;
}

function resolveFollowUp(actionSearch: any, payload: Record<string, any>) {
  if (typeof actionSearch === 'string') {
    return interpolateString(actionSearch, { payload });
  }
  if (actionSearch?.query) {
    return interpolateString(actionSearch.query, { payload });
  }
  return undefined;
}

function structuredCloneSafe(value: Record<string, any>): Record<string, any> {
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value));
  }
}
